import React, { useState } from "react";
import { Redirect } from "react-router-dom";
import { Form, SelectField, NumberField, SubmitButton, classnames } from "@culturehq/components";

import { useClickTransport } from "app/context";
import { t, Trans } from "app/utils";

import EventPortalButton from "../common/EventPortalButton";

import EventForm from "./EventForm";
import styles from "./EventForm.module.scss";

export const options = {
  openInvites: [
    { value: true, label: t("Yes - Guests can invite others") },
    { value: false, label: t("No - Only Hosts and Admins can invite others") }
  ],
  inviteOnly: [
    { value: true, label: t("Yes - The event will be visible in the platform, but only invited guests can RSVP") },
    { value: false, label: t("No - Anyone can RSVP to your event") }
  ],
  createSurvey: [
    { value: true, label: t("Yes - Users will rate/review their experience") },
    { value: false, label: t("No - Users will only be prompted to confirm their RSVP") }
  ],
  hostOnlySurvey: [
    { value: false, label: t("Yes – share with admins") },
    { value: true, label: t("No – only visible to me") }
  ]
};

const capValidator = value => {
  if (value && parseInt(value, 10) < 1) {
    return t("If set, maximum attendees can not be less than 0");
  }
  return null;
};

const AdvancedSettings = ({ eventState, handleEventState, onSubmit, handleUnSaved }) => {
  const [shareSurvey, setShareSurvey] = useState(true);
  const { onUsersClicked } = useClickTransport();

  if (!eventState.eventType) {
    return <Redirect to="/events/new/type" />;
  }

  const handleSubmit = async values => {
    await handleUnSaved(false);
    handleEventState(values);
    // Clean if there are selected users
    onUsersClicked([], null, null);
    return onSubmit(values);
  };

  const handleCreateSurvey = val => {
    handleUnSaved(true);
    setShareSurvey(val);
  };

  return (
    <EventForm icon="gear-wrench" title={t("Do you want to add optional custom settings?")}>
      <p className={classnames(styles["m-b-35"], styles["f-s-18"])}>
        <Trans>
          Adjust these settings to finalize the details of your event.
        </Trans>
      </p>
      <Form onSubmit={handleSubmit} initialValues={eventState} id="event_advance_settings_form" className="event-form">
        {!eventState.id && (
          <>
            <SelectField
              ariaLabel={t("Select if you want a post-event survey")}
              className="m-b-20"
              name="createSurvey"
              options={options.createSurvey}
              onChange={handleCreateSurvey}
              required
            >
              <Trans>Post-Event Survey</Trans>
            </SelectField>
            {shareSurvey && (
              <SelectField
                ariaLabel={t("Select if you want to share the survey results")}
                className="m-b-20"
                name="hostOnlySurvey"
                options={options.hostOnlySurvey}
                onChange={() => handleUnSaved(true)}
                required
              >
                <Trans>Share Survey Results</Trans>
              </SelectField>
            )}
          </>
        )}
        <SelectField
          ariaLabel={t("Select if the event is only for invited people")}
          name="inviteOnly"
          className="m-b-20"
          options={options.inviteOnly}
          onChange={() => handleUnSaved(true)}
          required
        >
          <Trans>Invite-Only</Trans>
        </SelectField>
        <SelectField
          ariaLabel={t("Select if guests can invite others")}
          className="m-b-20"
          name="openInvites"
          options={options.openInvites}
          onChange={() => handleUnSaved(true)}
          required
        >
          <Trans>Guests can Invite Others</Trans>
        </SelectField>
        <NumberField
          className="m-b-20"
          name="cap"
          min={1}
          validator={capValidator}
          onChange={() => handleUnSaved(true)}
          placeholder={t("Choose the maximum number of guests")}
        >
          <Trans>Attendee Limit</Trans>
        </NumberField>
        <EventPortalButton>
          {eventState.id
            ? (
              <SubmitButton primary form="event_advance_settings_form">
                {submitting => (submitting ? t("Saving...") : t("Save Event"))}
              </SubmitButton>
            )
            : (
              <SubmitButton primary form="event_advance_settings_form">
                {submitting => (submitting ? t("Go next...") : t("Publish Event"))}
              </SubmitButton>
            )}
        </EventPortalButton>
      </Form>
    </EventForm>
  );
};

export default AdvancedSettings;
